import { createContext, useState, useEffect, useCallback } from 'react';
import config from '../config';
import platformService from '../services/platformService';

export const PlatformAuthContext = createContext(null);

export function PlatformAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Set tokens in localStorage
  const setTokens = useCallback((accessToken, refreshToken) => {
    if (accessToken) {
      localStorage.setItem(config.storageKeys.accessToken, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(config.storageKeys.refreshToken, refreshToken);
    }
  }, []);

  // Clear tokens from localStorage
  const clearTokens = useCallback(() => {
    localStorage.removeItem(config.storageKeys.accessToken);
    localStorage.removeItem(config.storageKeys.refreshToken);
    sessionStorage.removeItem(config.storageKeys.adminSession);
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      const response = await platformService.login(email, password);

      if (response.success && response.data) {
        const { accessToken, refreshToken, admin: adminData } = response.data;

        setTokens(accessToken, refreshToken);
        setAdmin(adminData);
        setIsAuthenticated(true);

        // Cache admin data in session storage for faster reloads
        sessionStorage.setItem(config.storageKeys.adminSession, JSON.stringify(adminData));

        return { success: true };
      }

      return { success: false, error: 'Login failed' };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, error: message };
    }
  }, [setTokens]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem(config.storageKeys.refreshToken);
      if (refreshToken) {
        await platformService.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
      setAdmin(null);
      setIsAuthenticated(false);
    }
  }, [clearTokens]);

  // Check if admin has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!admin || !admin.permissions) return false;
    // Wildcard '*' grants all permissions
    if (admin.permissions.includes('*')) return true;
    return admin.permissions.includes(permission);
  }, [admin]);

  const isDealer = () => admin?.role === 'dealer';
  const isSuperAdmin = () => admin?.role === 'super_admin' || !admin?.role;

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem(config.storageKeys.accessToken);

      if (!accessToken) {
        setLoading(false);
        return;
      }

      // Try to get cached admin data first
      const cachedAdmin = sessionStorage.getItem(config.storageKeys.adminSession);
      if (cachedAdmin) {
        try {
          setAdmin(JSON.parse(cachedAdmin));
          setIsAuthenticated(true);
        } catch (e) {
          console.error('Failed to parse cached admin data:', e);
        }
      }

      // Verify token with server
      try {
        const response = await platformService.getMe();
        if (response.success && response.data?.admin) {
          setAdmin(response.data.admin);
          setIsAuthenticated(true);
          sessionStorage.setItem(config.storageKeys.adminSession, JSON.stringify(response.data.admin));
        } else {
          clearTokens();
          setAdmin(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth verification failed:', error);
        clearTokens();
        setAdmin(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [clearTokens]);

  // Listen for auth expired events
  useEffect(() => {
    const handleAuthExpired = () => {
      clearTokens();
      setAdmin(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('AUTH_EXPIRED', handleAuthExpired);
    return () => window.removeEventListener('AUTH_EXPIRED', handleAuthExpired);
  }, [clearTokens]);

  const value = {
    admin,
    isAuthenticated,
    loading,
    login,
    logout,
    hasPermission,
    isDealer,
    isSuperAdmin
  };

  return (
    <PlatformAuthContext.Provider value={value}>
      {children}
    </PlatformAuthContext.Provider>
  );
}
